/**
 * Renderers de pregunta por tipo — controles interactivos del wizard.
 * Estilos vía clases `aos-enc-*` (hoja embebida en EncuestaModal) sobre los
 * tokens de @aosenuma/ui (fallbacks inline para independencia del theme).
 */
import type { ReactNode } from 'react';
import type { EncuestaPregunta, EncuestaValor } from '@aosenuma/lib-types';

export interface PreguntaInputProps {
  pregunta: EncuestaPregunta;
  valor: EncuestaValor | undefined;
  onChange: (valor: EncuestaValor | undefined) => void;
}

const ESCALA_LABELS: Record<number, string> = {
  1: 'Muy en desacuerdo',
  2: 'En desacuerdo',
  3: 'Neutral',
  4: 'De acuerdo',
  5: 'Muy de acuerdo',
};

function Escala15({ valor, onChange }: PreguntaInputProps): ReactNode {
  const actual = typeof valor === 'number' ? valor : null;
  return (
    <div>
      <div className="aos-enc-escala" role="radiogroup" aria-label="Escala del 1 al 5">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={actual === n}
            className={`aos-enc-escala-btn${actual === n ? ' is-active' : ''}`}
            onClick={() => onChange(actual === n ? undefined : n)}
          >
            {n}
          </button>
        ))}
      </div>
      <div className="aos-enc-escala-caption">
        {actual ? ESCALA_LABELS[actual] : '1 = muy en desacuerdo · 5 = muy de acuerdo'}
      </div>
    </div>
  );
}

function Nps010({ valor, onChange }: PreguntaInputProps): ReactNode {
  const actual = typeof valor === 'number' ? valor : null;
  return (
    <div>
      <div className="aos-enc-nps" role="radiogroup" aria-label="Escala del 0 al 10">
        {Array.from({ length: 11 }, (_, n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={actual === n}
            data-zone={n <= 6 ? 'detractor' : n <= 8 ? 'pasivo' : 'promotor'}
            className={`aos-enc-nps-btn${actual === n ? ' is-active' : ''}`}
            onClick={() => onChange(actual === n ? undefined : n)}
          >
            {n}
          </button>
        ))}
      </div>
      <div className="aos-enc-escala-caption">0 = nada probable · 10 = totalmente probable</div>
    </div>
  );
}

function OpcionMultiple({ pregunta, valor, onChange }: PreguntaInputProps): ReactNode {
  const actual = typeof valor === 'string' ? valor : null;
  return (
    <div className="aos-enc-opciones" role="radiogroup">
      {pregunta.opciones.map((op) => (
        <button
          key={op}
          type="button"
          role="radio"
          aria-checked={actual === op}
          className={`aos-enc-chip${actual === op ? ' is-active' : ''}`}
          onClick={() => onChange(actual === op ? undefined : op)}
        >
          {op}
        </button>
      ))}
    </div>
  );
}

function SeleccionMultiple({ pregunta, valor, onChange }: PreguntaInputProps): ReactNode {
  const actual = Array.isArray(valor) ? valor : [];
  function toggle(op: string): void {
    const next = actual.includes(op) ? actual.filter((v) => v !== op) : [...actual, op];
    onChange(next.length > 0 ? next : undefined);
  }
  return (
    <div className="aos-enc-opciones" role="group">
      {pregunta.opciones.map((op) => {
        const on = actual.includes(op);
        return (
          <button
            key={op}
            type="button"
            role="checkbox"
            aria-checked={on}
            className={`aos-enc-chip${on ? ' is-active' : ''}`}
            onClick={() => toggle(op)}
          >
            <span className="aos-enc-chip-mark" aria-hidden="true">{on ? '✓ ' : ''}</span>
            {op}
          </button>
        );
      })}
    </div>
  );
}

function SiNo({ valor, onChange }: PreguntaInputProps): ReactNode {
  const actual = typeof valor === 'boolean' ? valor : null;
  return (
    <div className="aos-enc-sino" role="radiogroup">
      <button
        type="button"
        role="radio"
        aria-checked={actual === true}
        className={`aos-enc-sino-btn${actual === true ? ' is-active' : ''}`}
        onClick={() => onChange(actual === true ? undefined : true)}
      >
        Sí
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={actual === false}
        className={`aos-enc-sino-btn${actual === false ? ' is-active' : ''}`}
        onClick={() => onChange(actual === false ? undefined : false)}
      >
        No
      </button>
    </div>
  );
}

function Texto({ valor, onChange }: PreguntaInputProps): ReactNode {
  const actual = typeof valor === 'string' ? valor : '';
  return (
    <textarea
      className="aos-enc-textarea"
      value={actual}
      rows={3}
      maxLength={2000}
      placeholder="Escribe tu respuesta…"
      onChange={(e) => {
        const v = e.target.value;
        onChange(v.trim().length > 0 ? v : undefined);
      }}
    />
  );
}

export function PreguntaInput(props: PreguntaInputProps): ReactNode {
  switch (props.pregunta.tipo) {
    case 'escala_1_5':
      return <Escala15 {...props} />;
    case 'nps_0_10':
      return <Nps010 {...props} />;
    case 'opcion_multiple':
      return <OpcionMultiple {...props} />;
    case 'seleccion_multiple':
      return <SeleccionMultiple {...props} />;
    case 'si_no':
      return <SiNo {...props} />;
    case 'texto':
      return <Texto {...props} />;
  }
}
